import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, VStack, HStack, Text, Button, Spinner, Table, Badge } from '@chakra-ui/react';
import { LuPlus } from 'react-icons/lu';
import { format } from 'date-fns';
import { AccountRecord } from '@/types/account';
import { ROLE_LABELS } from '@/types/user';
import { accountService } from '@/services/firebase/accountService';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { DeleteAccountDialog } from '@/components/account/DeleteAccountDialog';
import { toaster } from '@/components/ui/toaster';

/**
 * アカウント一覧ページ
 */
export const AccountListPage: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [accounts, setAccounts] = useState<AccountRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);

  // 削除ダイアログ
  const [deleteTarget, setDeleteTarget] = useState<AccountRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await accountService.listAccounts();
      setAccounts(data);
    } catch (error) {
      toaster.error({
        title: 'アカウント一覧の取得に失敗しました',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchAccounts();
    }
  }, [user]);

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);
      await accountService.deleteAccount(deleteTarget.uid);
      toaster.success({ title: `${deleteTarget.userName} を削除しました` });
      setDeleteTarget(null);
      fetchAccounts();
    } catch (error) {
      toaster.error({
        title: 'アカウントの削除に失敗しました',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  /** ISO文字列日時を表示用にフォーマット */
  const formatDate = (iso: string): string => {
    if (!iso) return '-';
    try {
      return format(new Date(iso), 'yyyy/MM/dd');
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <VStack align="stretch" gap={6}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <Text fontSize="2xl" fontWeight="bold">
            アカウント管理
          </Text>
          <Button colorPalette="blue" onClick={() => navigate('/admin/accounts/new')}>
            <LuPlus />
            新規アカウント
          </Button>
        </HStack>

        {/* アカウント一覧 */}
        {accounts.length === 0 ? (
          <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
            <Text color="gray.500">アカウントがありません</Text>
          </Box>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>メールアドレス</Table.ColumnHeader>
                  <Table.ColumnHeader>ユーザー名</Table.ColumnHeader>
                  <Table.ColumnHeader>権限</Table.ColumnHeader>
                  <Table.ColumnHeader>作成日</Table.ColumnHeader>
                  <Table.ColumnHeader>操作</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {accounts.map((account) => {
                  const isSelf = account.uid === user?.uid;
                  return (
                    <Table.Row key={account.uid}>
                      <Table.Cell>
                        <Text fontWeight="medium">{account.email}</Text>
                      </Table.Cell>
                      <Table.Cell>{account.userName}</Table.Cell>
                      <Table.Cell>
                        <Badge colorPalette={account.role === 'admin' ? 'purple' : 'gray'}>
                          {ROLE_LABELS[account.role]}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{formatDate(account.createdAt)}</Table.Cell>
                      <Table.Cell>
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() =>
                              navigate(`/admin/accounts/${account.uid}/edit`, {
                                state: { account },
                              })
                            }
                          >
                            編集
                          </Button>
                          <Button
                            size="xs"
                            colorPalette="red"
                            variant="outline"
                            disabled={isSelf}
                            title={isSelf ? '自分自身のアカウントは削除できません' : undefined}
                            onClick={() => setDeleteTarget(account)}
                          >
                            削除
                          </Button>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </VStack>

      {/* 削除確認ダイアログ */}
      {deleteTarget && (
        <DeleteAccountDialog
          account={deleteTarget}
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          onDelete={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </AppLayout>
  );
};

export default AccountListPage;
